import { DOCUMENT } from '@angular/common';
import { BreakpointObserver, BreakpointState, Breakpoints } from '@angular/cdk/layout';
import { Inject, Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationListDialogComponent } from 'app/layout/common/confirmation-list-dialog/confirmation-list-dialog.component';
import { ConfirmationListDialogData } from 'app/layout/common/confirmation-list-dialog/confirmation-list-dialog.types';
import { UpdateService } from 'app/shared/services/update.service';
import { Observable, take } from 'rxjs';
import 'hammerjs';

@Injectable({ providedIn: 'any' })
export class MatDialogService {
    private readonly _updateService = inject(UpdateService);
    isExtraSmall$: Observable<BreakpointState> = this._breakpointObserver.observe(Breakpoints.XSmall);

    constructor(
        private _breakpointObserver: BreakpointObserver,
        private _dialog: MatDialog,
        @Inject(DOCUMENT) private readonly _document: Document
    ) {
    }

    openConfirmationListDialog(data: ConfirmationListDialogData, width = 520): MatDialogRef<ConfirmationListDialogComponent> {
        return this.open(ConfirmationListDialogComponent, data, width);
    }

    open(component: any, data: any = {}, width?: number): MatDialogRef<any> {
        const dialogRef = this._dialog.open(component, {
            data: data || null,
            maxHeight: '100vh',
            maxWidth: '100vw',
            autoFocus: false
        });

        // Add swipe event listener to the dialog overlay
        this._addSwipeListener(dialogRef);

        const dialogSize$ = this.isExtraSmall$.subscribe(size => {
            if (size.matches) {
                dialogRef.updateSize('100%', '100%');
                dialogRef.addPanelClass('custom-dialog-container');
            } else {
                dialogRef.updateSize(width ? `${width}px` : '600px', 'auto');
                dialogRef.removePanelClass('custom-dialog-container');
            }
        });

        this._document.body.classList.add('no-scroll'); // Add a class to the body to prevent scrolling when dialog is open
        dialogRef.afterClosed().subscribe(() => {
            this._document.body.classList.remove('no-scroll');
            dialogSize$.unsubscribe();
            this._updateService.applyStagedUpdate();
        });

        return dialogRef;
    }

    close(dialogRef: MatDialogRef<any>, withSlideOut: boolean = false, result?: unknown): void {
        if (withSlideOut) {
            dialogRef.addPanelClass('slide-out');
            setTimeout(() => dialogRef.close(result), 300);
        }
        else {
            dialogRef.close(result);
        }
    }

    private _addSwipeListener(dialogRef: MatDialogRef<any>): void {
        // Access the overlay element to attach swipe event listeners
        dialogRef.afterOpened().subscribe(() => {
            const overlayContainerElement = this._document.querySelector('.cdk-overlay-pane') as HTMLElement;

            if (!overlayContainerElement) {
                console.error('Overlay element not found');
                return;
            }

            // Listen for swipe right gesture using HammerJS
            const hammer = new Hammer(overlayContainerElement);

            // Optional: Configure swipe to recognize only horizontal swipes
            hammer.get('swipe').set({ direction: Hammer.DIRECTION_HORIZONTAL });

            // Handle swipe right event
            hammer.on('swiperight', ($event) => {
                this.close(dialogRef, true);
            });

            // Cleanup HammerJS instance on dialog close
            dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
                hammer.destroy();
            });
        });
    }
}
